<script setup lang="ts">
import { type Component, computed } from 'vue';
import { CameraIcon } from '#shared/components/icons';
import {
  getAdaptivePropertiesClassList,
  type AdaptiveProperty,
} from '#shared/lib/breakpoints';
import { useImageUpload } from '#shared/composables/useImageUpload';

const props = withDefaults(
  defineProps<{
    name?: string;
    src?: string | null;
    size?: AdaptiveProperty<'sm' | 'md' | 'lg'>;
    changeable?: boolean;
    error?: boolean;
    fallbackIcon?: Component;
    clickable?: boolean;
    stubBackground?: 'white' | 'light';
  }>(),
  {
    name: undefined,
    src: undefined,
    size: 'md',
    changeable: false,
    error: false,
    fallbackIcon: CameraIcon,
    clickable: false,
    stubBackground: 'white',
  }
);
const emit = defineEmits<{
  (e: 'change', file: File): void;
}>();

const { uploadFile } = useImageUpload((files) => emit('change', files[0]));

const propertyClasses = computed(() =>
  getAdaptivePropertiesClassList({
    'app-avatar_size': props.size,
  })
);
</script>

<template>
  <div
    :class="[
      'app-avatar',
      propertyClasses,
      { 'app-avatar_changeable': props.changeable },
      { 'app-avatar_error': props.error },
      { 'app-avatar_clickable': props.clickable },
      `app-avatar_stub-${props.stubBackground}`,
    ]"
    v-on="props.changeable ? { click: uploadFile } : {}"
  >
    <component :is="props.fallbackIcon" />
    <NuxtImg
      v-if="props.src"
      class="app-avatar__content"
      :src="props.src"
      :alt="props.name"
      sizes="sm:44px md:88px lg:150px"
    />
    <div v-else-if="props.name" class="app-avatar__content">
      {{ props.name[0] }}
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '#shared/assets/core.scss';

.app-avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--app-color-white);
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  aspect-ratio: 1/1;

  &_stub {
    &-white {
      background: var(--app-color-white);
    }
    &-light {
      background: var(--app-color-white-secondary);
    }
  }

  &_size {
    @include adaptive-property {
      &_sm {
        $size: stretch-size(375, 1920, 22, 44);
        width: $size;
        height: $size;
        font-size: calc($size / 3);
      }

      &_md {
        $size: stretch-size(375, 1920, 44, 88);
        width: $size;
        height: $size;
        font-size: calc($size / 3);
      }

      &_lg {
        $size: stretch-size(375, 1920, 88, 150);
        width: $size;
        height: $size;
        font-size: calc($size / 3);
      }
    }
  }

  svg {
    color: var(--app-color-primary);
    width: 1.5em;
    height: 1.5em;
  }

  &__content {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    color: var(--app-color-black-secondary);
    transition: 0.15s linear;
    display: flex;
    background-color: var(--app-color-white-secondary);
    justify-content: center;
    align-items: center;
  }

  &_changeable {
    cursor: pointer;
  }

  &_changeable:hover &__content {
    opacity: 50%;
  }

  &_error {
    border: 2px solid var(--app-color-red);
  }

  &_clickable {
    cursor: pointer;
  }
}
</style>
